import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '../../../core/services/data.service';
import { Subject } from 'rxjs';
import { GridOptions, CsvExportParams } from 'ag-grid-community';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '../../../store';
import { CellRenderersService } from '../../../core/services/cellRenderers.service';
import { UtilityService } from '../../../core/services/utility.service';
import { ApplicationEventsService } from '../../../core/services/application-events.service';
import { ValueGetterParams } from 'ag-grid-community';
import { map, tap, switchMap, takeUntil, filter } from 'rxjs/operators';
import { User } from '../../../core/models/User';
import { isNil } from 'lodash-es';
import { saveAs } from 'file-saver';

function genericFormatter(params: ValueGetterParams): any {
    const fieldName: string = params.colDef.field;
    const formattedFieldName = fieldName + 'Formatted';
    if (!params.data.hasOwnProperty(formattedFieldName)) {
        throw new Error(`field ${formattedFieldName} is missing for ${fieldName}`)
    }
    return params.data[formattedFieldName];
}


@Component({
  selector: 'app-visualisations',
  templateUrl: './visualisations.component.html',
  styleUrls: ['./visualisations.component.scss'],
  standalone: false
})
export class VisualisationsComponent implements OnInit, OnDestroy {
    isFxoSelected: boolean = false ;
    csvPath: string = null;
    csvShortPath: string = null;
    gridOptions: GridOptions;
    columnDef: any[];
    rowData: any[];
    searchText: string;
    inputDate: string = null;

    public unsubscribe: Subject<void> = new Subject();
    public gridApi;

    domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';


    otcFxpColumnDef: any[];
    otcEioColumnDef: any[];
    overlayLoadingTemplate: string;
    overlayNoRowsTemplate: string;

    invoiceAreBeingUpdated: boolean;
    showInvoiceAggregateFile: boolean;
    user: User = null;

  constructor(  private dataService: DataService,
                private route: ActivatedRoute,
                private router: Router,
                private store: Store,
                private cellRenderersService: CellRenderersService,
                private utilityService: UtilityService,
                private appEvents: ApplicationEventsService) {
        this.overlayLoadingTemplate = this.utilityService.overlayLoadingTemplate;
        this.overlayNoRowsTemplate = this.utilityService.overlayNoRowsTemplate;

        this.otcFxpColumnDef = [
            {headerName: 'Entity',                      field: 'entity',                    width: 110},    /*   1 */
            {headerName: 'Entity\'s Fund',              field: 'parNonPar',                 width: 110},    /*   2 */
            {headerName: 'Entity',                      field: 'entityName',                width: 110},    /*   3 */
            {headerName: 'Counterparty',                field: 'counterParty',              width: 180},    /*   4 */
            {headerName: 'Currency Pair',               field: 'currencyPair',              width: 150},    /*   5 */
            {headerName: 'Maturity Date',               field: 'deliveryQuarter',           width: 130},    /*   6 */
            {headerName: 'Instrument Type',             field: 'tradeType',                 width: 130},    /*   7 */
            {headerName: 'Trade Date',                  field: 'tradeDate',                 width: 180},    /*   8 */
            {headerName: 'Product Type',                field: 'productType',               width: 150},    /*   9 */
            {headerName: 'Delivery Date',               field: 'deliveryDate',              width: 150},    /*  10 */
            {headerName: 'Instrument ID',               field: 'tradeId',                   width: 150},    /*  11 */
            {headerName: 'Quotation Currency',          field: 'currency',                  width: 180},    /*  12 */
            {headerName: 'Notional Amount',             field: 'notionalAmount',            width: 150},    /*  13 */
            {headerName: 'Strike',                      field: 'tradeAllInRateStrike',      width: 150},    /*  14 */
            {headerName: 'Exchanged Currency',          field: 'againstCcy',                width: 150},    /*  15 */
            {headerName: 'Exchanged Amount',            field: 'againstAmount',             width: 150},    /*  16 */
            {headerName: 'Market Spot Rate',            field: 'marketSpotRate',            width: 150},    /*  17 */
            {headerName: 'Market All In Rate',          field: 'marketAllInRate',           width: 150},    /*  18 */
            {headerName: 'Exchanged Currency',          field: 'functionalCcy',             width: 150},    /*  19 */
            {headerName: 'Benchmark NPV Functional',    field: 'benchmarkNpvFunctional',    width: 150},    /*  20 */
            {headerName: 'Against NPV Functional',      field: 'againstNpvFunctional',      width: 150},    /*  21 */
            {headerName: 'NPV Exchanged Currency',      field: 'netNpvFunctional',          width: 150},    /*  22 */
            {headerName: 'Reporting Currency',          field: 'baseCcy',                   width: 150},    /*  23 */
            {headerName: 'Benchmark NPV Base',          field: 'benchmarkNpvBase',          width: 150},    /*  24 */
            {headerName: 'Against NPV Base',            field: 'againstNpvBase',            width: 150},    /*  25 */
            {headerName: 'NPV Reporting Currency',      field: 'netNpvBase',                width: 150},    /*  26 */
            {headerName: 'NPV Base',                    field: 'npvBase',                   width: 150},    /*  27 */
            {headerName: 'Instrument Name',             field: 'tradedescription',          width: 150},    /*  28 */
            {headerName: 'Delivery Type',               field: 'deliverySettleType',        width: 150},    /*  29 */
            {headerName: 'Alternate Trade Id',          field: 'alternateTradeId',          width: 150},    /*  30 */
            {headerName: 'Buy/Sell',                    field: 'buySell',                   width: 150},    /*  31 */
            {headerName: 'Option Type',                 field: 'optPutCall',                width: 150},    /*  32 */
        ];

        this.otcEioColumnDef = [
            {headerName: 'Instrument ID',               field: 'tradeID',                   width: 150},    /*   1 */
            {headerName: 'Instrument Name',             field: 'desc',                      width: 150},    /*   2 */
            {headerName: 'Counterparty',                field: 'counterParty',              width: 150},    /*   3 */
            {headerName: 'Trade Date',                  field: 'tradeDate',                 width: 150},    /*   4 */
            {headerName: 'Maturity Date',               field: 'expiryDate',                width: 150},    /*   5 */
            {headerName: 'Currency',                    field: 'currency',                  width: 150},    /*   6 */
            {headerName: 'Notional',                    field: 'notional',                  width: 150},    /*   7 */
            {headerName: 'Exercise Type',               field: 'exerciceType',              width: 150},    /*   8 */
            {headerName: 'Underlying ID',               field: 'equityID',                  width: 150},    /*   9 */
            {headerName: 'Strike Buy',                  field: 'strikeBuy',                 width: 150},    /*  10 */
            {headerName: 'Strike Sell',                 field: 'strikeSell',                width: 150},    /*  11 */
            {headerName: 'Market Value (in instrument Currency)',field: 'marketValueInLocalCcy',width: 150},/*  12 */
            {headerName: 'Market Value Buy',            field: 'marketValueBuy',            width: 150},    /*  13 */
            {headerName: 'Market Value Sell',           field: 'marketValueSell',           width: 150},    /*  14 */
            {headerName: 'Delta',                       field: 'delta',                     width: 150},    /*  15 */
            {headerName: 'Vega',                        field: 'vega',                      width: 150},    /*  16 */
            {headerName: 'Gamma',                       field: 'gamma',                     width: 150},    /*  17 */
            {headerName: 'P&L in USD',                  field: 'pnlInUsd',                  width: 150},    /*  18 */
            {headerName: 'Face Amount',                 field: 'faceAmount',                width: 150},    /*  19 */
            {headerName: 'Counterparty ID',             field: 'counterParty',              width: 150},    /*  20 */
            {headerName: 'Entity',                      field: 'entity',                    width: 150},    /*  21 */
            {headerName: 'Portfolio Code',              field: 'portfolio',                 width: 150},    /*  22 */
            {headerName: 'Trade Type',                  field: 'tradeType',                 width: 150},    /*  23 */
            {headerName: 'Portfolio Name',               field: 'trade',                    width: 150},    /*  24 */
            {headerName: 'Notional (in reporting currency)',field: 'notionalRpt',           width: 150},    /*  25 */
            {headerName: 'Reporting Currency',          field: 'baseCcy',                   width: 150},    /*  26 */
            {headerName: 'Market Value (in USD)',       field: 'mvUsd',                     width: 150},    /*  27 */
            {headerName: 'Contract Size',               field: 'optContSize',               width: 150},    /*  28 */
            {headerName: 'Leg 1 ID',                    field: 'fxoptLeg1Ids',              width: 150},    /*  29 */
            {headerName: 'Leg 2 ID',                    field: 'fxoptLeg2Ids',              width: 150},    /*  30 */
            {headerName: 'Strike 1',                    field: 'optStrike1Px',              width: 150},    /*  31 */
            {headerName: 'Strike 2',                    field: 'optStrike2Px',              width: 150},    /*  32 */
            {headerName: 'Leg 1 Type',                  field: 'optPut1Call',               width: 150},    /*  33 */
            {headerName: 'Leg 2 Type',                  field: 'optPut2Call',               width: 150},    /*  34 */
            {headerName: 'Leg 1 Direction',             field: 'direction1',                width: 150},    /*  35 */
            {headerName: 'Leg 2 Direction',             field: 'direction2',                width: 150},    /*  36 */
            {headerName: 'Price 1',                     field: 'optPx1',                    width: 150},    /*  37 */
            {headerName: 'Price 2',                     field: 'optPx2',                    width: 150},    /*  38 */
        ];
        this.initialiseGridOption();
    }


    ngOnInit() {
        this.invoiceAreBeingUpdated = false;
        this.showInvoiceAggregateFile = false;
        this.subscribeOnPageReload();
        this.subscribeUserProfileChange();
        //console.log("User is superAdmin :" + this.user.isSuperAdmin);
        this.reloadPageData();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public subscribeOnPageReload() {
        this.appEvents.pageReload
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.reloadPageData();
          });
    }

    public reloadPageData() {
        this.isFxoSelected = false;
        this.loadGridData();
    }


    public selectCsvFile(fileItems: any[]): string {
        let pattern = 'OTC_EIO_';
        if (this.isFxoSelected) {
            pattern = 'OTC_FXP_';
        }
        const found = fileItems.map(item => item.filePath).filter(path => path.indexOf(pattern) >= 0);
        if (!found || found.length != 1) {
            alert('cannot compute the CSV path');
            return null;
        }
        return found[0];
    }

    public setCsvPath(path) {
        this.csvPath = path;
        const offset = path.indexOf('/OTC/');
        this.csvShortPath = path.slice(offset + 5);
    }
    public loadGridData() {
        this.csvShortPath = '';
        this.csvPath = '';
        this.columnDef = null;
        this.rowData = [];

        this.dataService.getLatestOtcFiles().pipe(
            map(obj => this.selectCsvFile(obj.latestFiles)),
            tap((path: string) => this.setCsvPath(path)),
            tap((path: string) => this.showGridOverlay()),
            switchMap(path => {
                if (this.isFxoSelected) {
                    return this.dataService.getOtcFxo(path);
                } else {
                    return this.dataService.getOtcEio(path);
                }
            })).pipe(takeUntil(this.unsubscribe)).subscribe(data => {
                if (this.isFxoSelected) {
                    this.rowData = data.otcFxo;
                    this.columnDef = this.otcFxpColumnDef;
                } else {
                    this.rowData = data.otcEio;
                    this.columnDef = this.otcEioColumnDef
                }
                this.hideGridOverlay();
            }, error => {
                this.rowData = [];
                this.columnDef = null;
                alert(`Error happened, while trying to load CSV file ${this.csvPath}...`);
            });
    }

    public initialiseGridOption() {
        if (!this.gridOptions) {
            this.gridOptions = <GridOptions>{};
        }
        this.gridOptions.suppressRowDeselection = true;
    }

    onTableSelection(value) {
        this.isFxoSelected = value;
        this.loadGridData();
    }


    public showGridOverlay() {
        if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
        }
    }

    public hideGridOverlay() {
    if (this.gridApi) {
        this.gridApi.hideOverlay();
    }
  }


  public getGridCsvExportParam(): CsvExportParams {
        const param = <CsvExportParams>{};
        const formatter = this.utilityService.csvFormatter;
        param.processCellCallback = function (columnParams) {
            let val: any;
            const column = columnParams.column.getColDef();
            if (column && column.field && column.field === 'legalEntity' && columnParams.value != null) {
                val = columnParams.value.dataName;
            } else {
                val = columnParams.value;
            }
            return formatter(val);
        };
        return param;
    }

    onVisuExport() {
        this.gridApi.exportDataAsCsv(this.getGridCsvExportParam());
    }

    onGridReady(params) {
        this.gridApi = params.api;
        //this.gridApi.setGridAutoHeight(true);
        this.gridApi.sizeColumnsToFit();
    }

    onInvoiceRun() {
        console.log("In onInvoiceRun");
        const message = 'Cannot aggregate invoice';
        const newInputDate = this.inputDate.replace(/-/g, '');
        const req: any = { inputDate: newInputDate };
        console.log("Run : " + req.inputDate + " , original format is " + this.inputDate);
        const res = this.dataService.runInvoicesAggregation(req).pipe(takeUntil(this.unsubscribe)).subscribe(
            success => {
                if (success) {
                    this.invoiceAreBeingUpdated = false;
                    this.showInvoiceAggregateFile = true;
                } else {
                    alert(message);
                    this.invoiceAreBeingUpdated = false;
                    this.showInvoiceAggregateFile = false;
                }
            },
            error => {
                alert(message + " : " + error);
                this.invoiceAreBeingUpdated = false;
                this.showInvoiceAggregateFile = false;
            }
        );
        this.invoiceAreBeingUpdated = true;
    }

    onInvoicesDownload() {
        const newInputDate = this.inputDate.replace(/-/g, '');
        const req: any = { inputDate: newInputDate };
        console.log("Download aggregate invoice file press : " + req.inputDate + " , original format is " + this.inputDate);
        this.dataService.downloadInvoicesFile(req).subscribe((data) => {
            console.log('received download data. now saving file...');
            saveAs(data, 'invoices.zip');
        }, (error) => {
            console.log('download failed. error: ' + error)
        }
        );
    }

    public subscribeUserProfileChange() {
        this.store.select<User>('user').pipe(
          filter(Boolean),
          takeUntil(this.unsubscribe)
        ).subscribe((user: User) => {
          this.user = user;
        });
      }

}